import { configureStore, getDefaultMiddleware } from "@reduxjs/toolkit"
import { persistStore, persistReducer, createTransform } from "redux-persist"
import storage from "redux-persist/lib/storage" // defaults to localStorage for web
import rootReducer from "./reducers/rootReducer" // Import your root reducer

/**
 * Keep the project's full meter list out of localStorage.
 *
 * The HES module stores that list twice — `responseData` (raw API response) and `meterList`
 * (the same rows mapped to select options). With no whitelist, redux-persist was serialising
 * both to localStorage on every state change and JSON.parsing them before the app could
 * paint, which is a large part of why login and refresh sat on a blank screen.
 *
 * Dropping them is safe: `meterList` has no readers anywhere, and `responseData` has a single
 * reader inside HES, whose mount effect refetches and re-dispatches both unconditionally.
 * Everything else (projectList, spvList, gaList, chargeareaList, session) still persists.
 */
const stripBulkyMeterPayloads = createTransform(
  (inboundState) => {
    const next = { ...(inboundState || {}) }
    delete next.meterList
    delete next.responseData
    return next
  },
  (outboundState) => outboundState,
  { whitelist: ["meterList"] }
)

// Configuration for redux-persist
const persistConfig = {
  key: "root",
  storage,
  transforms: [stripBulkyMeterPayloads]
}

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer)

// Configure the store
const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware({
    serializableCheck: {
      // Ignore these action types
      ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
    },
  }),
})

// Create a persistor
const persistor = persistStore(store)

export { store, persistor }
